import {
  type Board,
  type BoardCell,
  type BoardEntry,
  type BoardMoment,
  boardGrid,
  cellCode,
  holdsStill,
  liveEntries,
  ratioValue,
} from '@app/shared'
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { OrgListItem } from '../../lib/org'
import { orgHref } from '../../lib/public-router'
import {
  KIOSK_ACTIVE_MESSAGE,
  KIOSK_BUSY_MESSAGE,
  KIOSK_DONE_MESSAGE,
  kioskHref,
} from '../kiosk'
import { BoardClock, BoardWidget } from './BoardWidgets'
import type { Leading } from './board-moment'
import { useKioskIdle } from './use-kiosk-idle'

export const givingClosesFor = (moment: BoardMoment | null): boolean =>
  moment?.rest === true

/**
 * A board drawn on its canvas: the ratio's snapping grid as tracks, and each
 * cell placed by the units it starts at and spans. Canvas no cell covers is
 * left blank.
 */
export function BoardGrid({
  board,
  shul,
  now,
  leading,
  moment,
  orgShortId,
  boardShortId,
  codes = false,
  online,
  children,
}: {
  board: Board
  shul: OrgListItem
  now: number
  leading: Leading | null
  moment: BoardMoment | null
  orgShortId: string
  boardShortId: string | null
  /** Label each zone with its code, to match a preview to the editor's map. */
  codes?: boolean
  online: boolean
  /** Laid over the board, inside its scope — the full-screen control. */
  children?: ReactNode
}) {
  const { layout } = board
  const grid = boardGrid(layout.ratio)
  const [flow, setFlow] = useState<{
    href: string
    choosing: boolean
    title: 'Give' | 'Pay my account'
  } | null>(null)
  const openedBy = useRef<HTMLElement | null>(null)
  const closeGiving = useCallback(() => setFlow(null), [])
  const { countdown, reset, hold } = useKioskIdle(flow !== null, closeGiving)
  const frame = useRef<HTMLIFrameElement>(null)
  const back = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (flow) back.current?.focus()
    else if (openedBy.current?.isConnected) openedBy.current.focus()
  }, [flow])

  useEffect(() => {
    if (givingClosesFor(moment)) closeGiving()
  }, [closeGiving, moment])

  useEffect(() => {
    if (!flow || flow.choosing) return
    const receive = (event: MessageEvent) => {
      if (
        event.origin !== location.origin ||
        event.source !== frame.current?.contentWindow
      )
        return
      if (event.data?.type === KIOSK_DONE_MESSAGE) closeGiving()
      else if (event.data?.type === KIOSK_ACTIVE_MESSAGE) reset()
      else if (event.data?.type === KIOSK_BUSY_MESSAGE)
        hold(event.data.active === true)
    }
    window.addEventListener('message', receive)
    return () => window.removeEventListener('message', receive)
  }, [closeGiving, flow, hold, reset])

  return (
    <div
      className="display-board"
      style={
        {
          // The board's own ratio, not the grid's: a unit need not be square,
          // and 24 by 14 is not 16:9.
          '--board-ratio': ratioValue(layout.ratio),
          gridTemplateColumns: `repeat(${grid.width}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${grid.height}, minmax(0, 1fr))`,
        } as CSSProperties
      }
    >
      <div className="display-board__behind" inert={flow ? true : undefined}>
        {layout.cells.map((cell) => {
          const style: CSSProperties = {
            gridColumn: `${cell.x + 1} / span ${cell.w}`,
            gridRow: `${cell.y + 1} / span ${cell.h}`,
          }
          return (
            <Zone
              key={cell.id}
              zone={cell}
              style={style}
              entries={board.zones[cell.id] ?? []}
              still={!!moment && holdsStill(board, moment)}
              shul={shul}
              leading={leading}
              moment={moment}
              orgShortId={orgShortId}
              boardShortId={boardShortId}
              code={codes ? cellCode(layout, cell) : null}
              now={now}
              online={online}
              onGive={(href, choosing, title) => {
                openedBy.current =
                  document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null
                setFlow({ href, choosing, title })
              }}
            />
          )
        })}
        {children}
      </div>
      {flow ? (
        <div
          className="display-board-kiosk"
          onPointerDown={reset}
          onKeyDown={reset}
          onWheel={reset}
          onScroll={reset}
        >
          {flow.choosing ? (
            <div className="display-board-kiosk__choice">
              <button
                type="button"
                className="display-board-give"
                onClick={() =>
                  setFlow({ ...flow, choosing: false, title: 'Give' })
                }
              >
                Give a gift
              </button>
              <button
                type="button"
                className="display-board-give"
                onClick={() =>
                  setFlow({
                    href: kioskHref(orgHref(orgShortId, 'pay'), true),
                    choosing: false,
                    title: 'Pay my account',
                  })
                }
              >
                Pay my account
              </button>
            </div>
          ) : (
            <iframe
              ref={frame}
              className="display-board-kiosk__frame"
              title={flow.title}
              src={flow.href}
              onLoad={() => {
                reset()
                const inside = frame.current?.contentWindow
                if (!inside) return
                for (const event of [
                  'pointerdown',
                  'keydown',
                  'scroll',
                  'wheel',
                ])
                  inside.addEventListener(event, reset, { passive: true })
              }}
            />
          )}
          <button
            ref={back}
            type="button"
            className="display-board-kiosk__back"
            onClick={closeGiving}
          >
            Back to the board
          </button>
          {countdown !== null ? (
            <button
              type="button"
              className="display-board-kiosk__countdown"
              onClick={reset}
            >
              Still there? Tap to keep going ({countdown})
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * One zone, rotating whatever of its playlist is live at the moment.
 *
 * Until the moment is known nothing is decided, so the zone stays empty rather
 * than showing an entry that may turn out to be outside its window.
 */
function Zone({
  zone,
  style,
  entries,
  shul,
  leading,
  moment,
  orgShortId,
  boardShortId,
  code,
  still,
  now,
  online,
  onGive,
}: {
  zone: BoardCell
  style: CSSProperties
  entries: BoardEntry[]
  /** The board holds still: the zone keeps its first live entry. */
  still: boolean
  now: number
  online: boolean
  onGive: (
    target: string,
    choose: boolean,
    title: 'Give' | 'Pay my account',
  ) => void
  shul: OrgListItem
  leading: Leading | null
  moment: BoardMoment | null
  orgShortId: string
  boardShortId: string | null
  code: string | null
}) {
  const live = moment ? liveEntries(entries, moment) : []
  const [index, setIndex] = useState(0)
  const at = live.length && !still ? index % live.length : 0
  const current = live[at]

  // Keyed on the slot and how long it shows, never on the entry object: a
  // re-read hands back equal entries as new objects, and an entry's turn must
  // not restart each time one lands.
  const seconds = current?.seconds ?? 0
  useEffect(() => {
    if (still || live.length < 2 || seconds === 0) return
    const id = window.setTimeout(() => setIndex(at + 1), seconds * 1000)
    return () => window.clearTimeout(id)
  }, [still, live.length, at, seconds])

  return (
    <section
      className="display-board__zone"
      data-zone={zone.id}
      data-page={current?.widget.kind === 'page' || undefined}
      style={style}
    >
      {current ? (
        <Slide key={at} measure={current.widget.kind !== 'page'}>
          {current.widget.kind === 'clock' ? (
            <BoardClock
              shul={shul}
              now={now}
              leading={leading}
              face={current.widget.face}
            />
          ) : (
            <BoardWidget
              widget={current.widget}
              shul={shul}
              leading={leading}
              orgShortId={orgShortId}
              boardShortId={boardShortId}
              onDate={moment?.date ?? null}
              online={online}
              onGive={onGive}
            />
          )}
        </Slide>
      ) : null}
      {code ? <span className="display-board__code">{code}</span> : null}
    </section>
  )
}

/** The least a slide shrinks to; past it, the end of a long list is cut. */
const FIT_FLOOR = 0.5

/**
 * One entry on screen, shrunk until it fits its zone.
 *
 * Nobody scrolls a board, so content longer than its zone either fits or is
 * lost. The slide's unit is scaled down — every size in it is a multiple of
 * that unit, so it shrinks whole — by halving the interval between the
 * largest scale that overflows and the smallest that fits. Measured again
 * whenever the zone changes size or what is drawn inside it changes — the
 * second watched as the DOM changing rather than as a size, because the
 * measurement itself resizes the content and would answer itself for ever.
 */
function Slide({
  children,
  measure,
}: {
  children: ReactNode
  measure: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!measure) return
    const el = ref.current
    if (!el) return
    const fit = () => {
      const overflows = (scale: number) => {
        el.style.setProperty('--board-fit', String(scale))
        return el.scrollHeight > el.clientHeight + 1
      }
      if (!overflows(1)) return
      let lo = FIT_FLOOR
      let hi = 1
      for (let i = 0; i < 7; i++) {
        const mid = (lo + hi) / 2
        if (overflows(mid)) hi = mid
        else lo = mid
      }
      el.style.setProperty('--board-fit', String(lo))
    }
    fit()
    const resized = new ResizeObserver(fit)
    if (el.parentElement) resized.observe(el.parentElement)
    const changed = new MutationObserver(fit)
    changed.observe(el, { childList: true, subtree: true, characterData: true })
    return () => {
      resized.disconnect()
      changed.disconnect()
    }
  }, [measure])

  return (
    <div ref={ref} className="display-board__slide">
      {children}
    </div>
  )
}
